import get from "lodash/get";

import ProductOptionSelector from "@components/store/product-detail/shared/product-option-selector";
import QuantitySelector from "@components/store/product-detail/shared/quantity-selector";
import VendorHolidayMessage from "@components/store/product-detail/shared/vendor-holiday-message";
import ProductNotAvailableMessage from "@components/store/product-detail/shared/product-not-available-message";
import PropertiesAccordionList from "@components/store/product-detail/shared/properties-accordion-list";
import ProductInfoSection from "@components/store/product-detail/shared/product-info-section";
import ProductDescriptionAccordion from "@components/store/product-detail/shared/accordion/product-description";
import { getProductType, getProductCategory } from "@helpers/products/types";

import useShowPrefrenceRestrictionModal from "@hooks/product-detail/use-show-prefrence-restriction-modal";

import { useProductInfoContext } from "@contexts/product-info-context";
import { ANALYTICS_ATTRIBUTE_TYPE } from "@constants/analytics/shop";

import styles from "./main.module.scss";
import { useContext } from "react";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

const giftCardFallbackInstruction =
  "Gift cards will be emailed to you within 48-72 hours after checkout to avoid fraudulent activity.";

export default function GiftCardDetail({
  product,
  optionTypesData,
  variantValues,
  optionTypeVariantMapping,
  setOptionTypeVariantMapping,
  productQuantity,
  setProductQuantity,
  canBuyProduct,
  renderProductDetailHeader,
}) {
  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const { name, vendorByCountry } = product;

  const {
    hideVariantSelectionSection,
    showAddToBox,
    isViewOnlyMode,
    renderCatalogModeFooterLinks,
    onSuccessCallback,
  } = useProductInfoContext();

  const { renderRestrictionsModal, renderAddToBagBtn } = useShowPrefrenceRestrictionModal({
    product,
    selectedVariant: optionTypeVariantMapping,
    isBYOProduct: showAddToBox,
  });

  const handleQuantity = (newQuantity) => {
    setProductQuantity(newQuantity);
  };

  const renderPriceVariants = () => {
    if (!optionTypesData.rewards) return null;
    const title = get(variantValues[0], "optionTypePresentation", "Egift Card Amount");
    return (
      <ProductOptionSelector
        title={title}
        availData={variantValues}
        selectedData={optionTypeVariantMapping}
        handleChange={setOptionTypeVariantMapping}
        intent={product.intent}
        disabled={!canBuyProduct}
        eventLocationAnalytics={{
          track: true,
          eventProperties: {
            product_id: product.id,
            attribute_type: ANALYTICS_ATTRIBUTE_TYPE.AMOUNT,
            product_type: getProductType(product),
            category: getProductCategory(product),
          },
        }}
      />
    );
  };
  const renderQuantitySelector = () => (
    <div className={styles.qtyColLeft}>
      <span className={styles.qtyWrapper}>Quantity</span>
      <QuantitySelector
        quantity={productQuantity}
        setQuantity={setProductQuantity}
        className="defaultStyle"
        disabled={!canBuyProduct}
        eventLocationAnalytics={{
          track: true,
          eventProperties: {
            product_id: product.id,
            product_type: getProductType(product),
            category: getProductCategory(product),
          },
        }}
      />
    </div>
  );

  const updatedProduct = { ...product, priceIn: optionTypeVariantMapping.priceIn };

  const addToBagProps = {
    product: updatedProduct,
    quantity: productQuantity,
    variant: optionTypeVariantMapping,
    btnParentClass: styles.bagBtnBlock,
    showAddToBox,
    onSuccessCallback,
    handleQuantity,
    isDisabled: !canBuyProduct,
  };

  return (
    <div className={`${styles.boxContainerFluid} boxContainerFluid`}>
      {!isMobileDevice && renderProductDetailHeader()}
      <VendorHolidayMessage vendor={vendorByCountry} />
      {!hideVariantSelectionSection && renderPriceVariants()}
      <ProductNotAvailableMessage canBuyProduct={canBuyProduct} name={name} />
      {!(hideVariantSelectionSection || isViewOnlyMode) && (
        <div className={`${styles.qtyWithBagBtnRow} stickyDemoMode`}>
          {renderQuantitySelector()}
          {renderAddToBagBtn(addToBagProps)}
        </div>
      )}
      <ProductDescriptionAccordion product={product} />
      <PropertiesAccordionList
        instructions={product?.productDetail?.orderInstructions || giftCardFallbackInstruction}
        productId={product.id}
      />
      <ProductInfoSection product={product} />
      {!hideVariantSelectionSection &&
        renderRestrictionsModal({
          product: updatedProduct,
          quantity: productQuantity,
          variant: optionTypeVariantMapping,
        })}
      {renderCatalogModeFooterLinks()}
    </div>
  );
}
